import cn from "classnames"

import { Button, Card, Flex, Link } from "@/atoms"
import { TEASERS } from "@/const/teaser.constants"
import { isUserAdmin } from "@/domain/user"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags, useUser } from "@/hooks/useUser"
import { ConnectEnvData, FunctionExecuted, Runtime } from "@/icons"
import { Heading } from "@/molecules/Heading"
import { InformationPopover } from "@/molecules/InformationPopover"
import { checkInsightValue } from "@/molecules/InsightsIcons"
import { TAB_NAMES } from "@/organisms/Drawers/const"

import type { RuntimeEvidenceCardProps } from "./RuntimeEvidenceCard.types"

import styles from "./RuntimeEvidenceCard.module.scss"

export const RuntimeEvidenceCard = (props: RuntimeEvidenceCardProps) => {
  const { logger, EVENTS } = useLogger()
  const { user } = useUser()
  const { t } = useTranslation()
  const { t: tRuntimeEvidence } = useTranslation("translation", {
    keyPrefix: "runtimeEvidence"
  })
  const kortexInstalledFF = useFeatureFlags("kortexInstalled")
  const kortexInstalledProps =
    TEASERS.KORTEX_INSTALLED.noActiveSensorsDeployed(t)
  const showLink = isUserAdmin(user)
  const { record, drawerName, eventFrom } = props
  const runtimeData = record?.runtimeData
  const keyEvidence = runtimeData?.keyEvidence
  const isKortexInstaled = !!kortexInstalledFF
  const isOnPrem = record?.applicationsMeta?.isOnPrem
  const hasDisplayText = !!keyEvidence?.displayText

  return (
    <Card className={cn("embedded-card-ks", styles.card)}>
      <Flex gap={isKortexInstaled ? 12 : 24} align="center">
        <div>
          {isKortexInstaled ? (
            checkInsightValue(keyEvidence?.isExecuted ?? false) ? (
              <FunctionExecuted
                className={cn(styles.runtimeIcon, styles.executed)}
              />
            ) : (
              <Runtime
                className={cn(
                  styles.runtimeIcon,
                  !keyEvidence?.isRuntime && styles.notInRuntime
                )}
              />
            )
          ) : (
            <ConnectEnvData className={styles.connectEnvIcon} />
          )}
        </div>
        <Flex vertical>
          <Heading
            level={5}
            title={t("general.runtimeEvidence")}
            popover={{
              className: styles.popover,
              content: (
                <InformationPopover.CommonTemplate
                  classes={{ content: styles.contentPopover }}
                >
                  {tRuntimeEvidence("contentPopover")}
                </InformationPopover.CommonTemplate>
              )
            }}
          />
          {isKortexInstaled ? (
            <>
              {hasDisplayText && isOnPrem && (
                <Flex vertical>
                  <div className={styles.smText}>
                    {`${tRuntimeEvidence("no_runtime_indication")}.`}
                  </div>
                  <div className={styles.smText}>
                    {`${tRuntimeEvidence("local_ci_scan")}.`}
                  </div>
                </Flex>
              )}
              {hasDisplayText && !isOnPrem && (
                <div className={styles.smText}>{keyEvidence.displayText}</div>
              )}
              {!!runtimeData?.runtimeEvidence?.length && (
                <div className={styles.buttonWrapper}>
                  <Button
                    type="link"
                    className={styles.smText}
                    onClick={() => {
                      if (drawerName) {
                        logger.info(EVENTS.ANALYTIC_EVENTS.DRAWER.CHANGE_TAB, {
                          name: drawerName,
                          id: record?.id,
                          tab: TAB_NAMES.runtimeEvidence,
                          from: eventFrom || "cardLink"
                        })
                      }

                      props.actions.onChangeActiveKey(TAB_NAMES.runtimeEvidence)
                    }}
                  >
                    {tRuntimeEvidence("viewEvidence")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={styles.smText}>{kortexInstalledProps.title}</div>
              {showLink && (
                <Link
                  to={kortexInstalledProps.link.url}
                  className={styles.smText}
                >
                  {kortexInstalledProps.link.text}
                </Link>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Card>
  )
}
